
import { supabase } from '@/lib/supabase';
import { AIPrompt, PromptType } from '@/types/ai-prompts';

/**
 * Fetch all prompts of a specific type
 */
export const fetchPromptsByType = async (type: PromptType): Promise<AIPrompt[]> => {
  const { data, error } = await supabase
    .from('ai_prompts')
    .select('*')
    .eq('type', type)
    .order('name');
  
  if (error) {
    console.error(`Error fetching ${type} prompts:`, error);
    throw error;
  }
  
  return data || [];
};

/**
 * Fetch a specific prompt by type and name
 */
export const fetchPromptByTypeAndName = async (type: PromptType, name: string): Promise<AIPrompt | null> => {
  const { data, error } = await supabase
    .from('ai_prompts')
    .select('*')
    .eq('type', type)
    .eq('name', name)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') { // No rows returned
      return null;
    }
    console.error(`Error fetching prompt ${type}/${name}:`, error);
    throw error;
  }
  
  return data;
};

/**
 * Create a new prompt
 */
export const createPrompt = async (prompt: Omit<AIPrompt, 'id' | 'created_at' | 'updated_at'>): Promise<AIPrompt> => {
  const { data, error } = await supabase
    .from('ai_prompts')
    .insert(prompt)
    .select()
    .single();
  
  if (error) {
    console.error('Error creating prompt:', error);
    throw error;
  }
  
  return data;
};

/**
 * Update an existing prompt
 */
export const updatePrompt = async (id: string, updates: Partial<AIPrompt>): Promise<AIPrompt> => {
  const { data, error } = await supabase
    .from('ai_prompts')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating prompt:', error);
    throw error;
  }
  
  return data;
};

/**
 * Delete a prompt
 */
export const deletePrompt = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('ai_prompts')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting prompt:', error);
    throw error;
  }
};
